import { useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { DatePickerField, TimePickerField } from "@/components/DateTimePicker";
import { useLocale } from "@/i18n/LocaleContext";

export default function SafetyShareDialog({ offer }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const proposed = offer?.date_details?.proposed_date;
  const [checkinEnabled, setCheckinEnabled] = useState(false);
  const [checkinDate, setCheckinDate] = useState(proposed && proposed >= today ? proposed : today);
  const [checkinTime, setCheckinTime] = useState("23:00");

  const send = async () => {
    if (!email.trim() || sending) return;
    const payload = { email: email.trim(), note: note.trim() || undefined };
    if (checkinEnabled && checkinDate && checkinTime) {
      const when = new Date(`${checkinDate}T${checkinTime}`);
      if (when <= new Date()) {
        toast.error(t("safety.checkinPast"));
        return;
      }
      payload.checkin_at = when.toISOString();
    }
    setSending(true);
    try {
      await api.post(`/offers/${offer.offer_id}/safety-share`, payload);
      toast.success(payload.checkin_at ? t("safety.checkinScheduled") : t("safety.sent"));
      setOpen(false);
      setEmail("");
      setNote("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-emerald-400 hover:underline"
        data-testid={`safety-share-btn-${offer.offer_id}`}
      >
        <ShieldCheck className="w-3.5 h-3.5" /> {t("safety.share")}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="safety-share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-400" /> {t("safety.title")}
            </DialogTitle>
            <DialogDescription>{t("safety.desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              type="email"
              placeholder={t("safety.emailPh")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="safety-share-email"
            />
            <Textarea
              placeholder={t("safety.notePh")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              data-testid="safety-share-note"
            />
            <div className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-medium">{t("safety.checkinToggle")}</p>
                  <p className="text-[11px] text-muted-foreground">{t("safety.checkinDesc")}</p>
                </div>
                <Switch checked={checkinEnabled} onCheckedChange={setCheckinEnabled} data-testid="safety-checkin-toggle" />
              </div>
              {checkinEnabled && (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <DatePickerField value={checkinDate} onChange={setCheckinDate} id="safety-checkin-date" />
                  <TimePickerField value={checkinTime} onChange={setCheckinTime} id="safety-checkin-time" />
                </div>
              )}
            </div>
            <Button className="w-full" onClick={send} disabled={sending || !email.includes("@")} data-testid="safety-share-send">
              {t("safety.send")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
